import { observeServerDate } from "./serverClock";
import type { RequestTelemetry, TelemetryContextProvider } from "./telemetry";
import { endRequestSpanError, endRequestSpanSuccess, rowsFromResponse, startRequestSpan } from "./telemetry";

export type { RequestTelemetry, TelemetryContextProvider } from "./telemetry";

/**
 * One API request as the endpoint functions describe it. `path` is relative to the transport's
 * `baseUrl` and already interpolated; `telemetry` carries the static route template so spans stay
 * low-cardinality.
 */
export type ApiRequest = {
  method: string;
  path: string;
  query?: object;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  telemetry?: RequestTelemetry;
};

/** Every transport failure, normalized: HTTP errors keep their status; network failures use status 0. */
export class ApiError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public readonly body?: unknown;

  constructor(
    message: string,
    { status, code, body, cause }: { status: number; code?: string; body?: unknown; cause?: unknown },
  ) {
    super(message, cause === undefined ? undefined : { cause });
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

export type Transport = <T>(req: ApiRequest) => Promise<T>;

/** Ambient attributes (boot phase, etc.) merged into every request span. */
export interface TelemetryHooks {
  context?: TelemetryContextProvider;
}

/**
 * Builds the app's single request pipeline: auth header injection, cookie credentials, error
 * normalization to {@link ApiError}, 401 → `onUnauthorized`, and one telemetry span per request.
 * Deliberately no retry, caching, or dedupe — TanStack Query owns those.
 */
export function createTransport({
  baseUrl,
  getAuthHeaders,
  onUnauthorized,
  telemetry,
}: {
  baseUrl: string;
  getAuthHeaders: () => Promise<Record<string, string>>;
  onUnauthorized: () => void;
  telemetry?: TelemetryHooks;
}): Transport {
  return async <T>(req: ApiRequest): Promise<T> => {
    const span = req.telemetry ? startRequestSpan(req.telemetry, telemetry?.context?.()) : undefined;
    try {
      const { status, data } = await execute<T>(baseUrl, req, await getAuthHeaders(), onUnauthorized);
      if (span) {
        endRequestSpanSuccess(span, status, rowsFromResponse(data, req.telemetry?.rowsField));
      }
      return data;
    } catch (e) {
      if (span) {
        endRequestSpanError(span, e);
      }
      throw e;
    }
  };
}

async function execute<T>(
  baseUrl: string,
  req: ApiRequest,
  authHeaders: Record<string, string>,
  onUnauthorized: () => void,
): Promise<{ status: number; data: T }> {
  let response: Response;
  try {
    response = await fetch(`${baseUrl}${req.path}${toQueryString(req.query)}`, {
      method: req.method,
      credentials: "include",
      headers: {
        ...authHeaders,
        ...(req.body !== undefined && !(req.body instanceof FormData) ? { "Content-Type": "application/json" } : {}),
        ...req.headers,
      },
      body: serializeBody(req.body),
      signal: req.signal ?? null,
    });
  } catch (e) {
    // Aborts pass through untouched so callers (TanStack Query cancellation) can recognize them.
    if (e instanceof DOMException && e.name === "AbortError") {
      throw e;
    }
    throw new ApiError(e instanceof Error ? e.message : String(e), { status: 0, cause: e });
  }

  observeServerDate(response.headers.get("date"));
  const body = await parseBody(response);
  if (!response.ok) {
    if (response.status === 401) {
      onUnauthorized();
    }
    throw new ApiError(errorMessage(response, body), {
      status: response.status,
      code: errorCode(body),
      body,
    });
  }
  if (body instanceof BodyParseFailure) {
    // A 2xx whose body fails to parse (connection truncated between headers and body, browser-side
    // interference) must not masquerade as an empty response — see SVS-2478.
    throw new ApiError(`Response body parse failure (status ${response.status})`, {
      status: response.status,
      cause: body.cause,
    });
  }
  return { status: response.status, data: body as T };
}

class BodyParseFailure {
  constructor(public readonly cause: unknown) {}
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) {
    return undefined;
  }
  const text = await response.text().catch((e) => new BodyParseFailure(e));
  if (text instanceof BodyParseFailure || text === "") {
    return text || undefined;
  }
  try {
    return JSON.parse(text);
  } catch (e) {
    // Error bodies may legitimately be plain text; a non-JSON success body is a parse failure.
    return response.ok ? new BodyParseFailure(e) : text;
  }
}

function errorMessage(response: Response, body: unknown): string {
  if (body !== null && typeof body === "object") {
    const { message, error } = body as { message?: unknown; error?: unknown };
    if (typeof message === "string" && message) {
      return message;
    }
    if (typeof error === "string" && error) {
      return error;
    }
  }
  return `Request failed with status ${response.status}${response.statusText ? ` (${response.statusText})` : ""}`;
}

function errorCode(body: unknown): string | undefined {
  if (body === null || typeof body !== "object") {
    return undefined;
  }
  const { code, errorCode: legacyCode } = body as { code?: unknown; errorCode?: unknown };
  if (typeof code === "string") {
    return code;
  }
  return typeof legacyCode === "string" ? legacyCode : undefined;
}

function serializeBody(body: unknown): BodyInit | null {
  if (body === undefined || body === null) {
    return null;
  }
  return body instanceof FormData ? body : JSON.stringify(body);
}

function toQueryString(query: object | undefined): string {
  if (!query) {
    return "";
  }
  // `null` drops out alongside `undefined`: the generated operations type nullable spec parameters
  // as `T | null`, and a literal `page=null` on the wire is never what the caller meant.
  const parts = Object.entries(query)
    .filter(([, value]) => value != null)
    .flatMap(([key, value]) =>
      (Array.isArray(value) ? value : [value]).map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`),
    );
  return parts.length ? `?${parts.join("&")}` : "";
}
